import { useState, useEffect } from 'react';
import EntranceHall from './components/EntranceHall';
import NavigationMap from './components/NavigationMap';
import LightControlPanel from './components/LightControlPanel';
import ArtworkGallery from './components/ArtworkGallery';
import WingRoom from './components/WingRoom';
import InformationOverlay from './components/InformationOverlay';
import ImageUploadManager from './components/ImageUploadManager';
import ContactForm from './components/ContactForm';
import { supabase } from '../../lib/supabaseClient';

interface CustomArtwork {
  id: number;
  title: string;
  artist: string;
  year: string;
  medium: string;
  dimensions: string;
  imageUrl: string;
  description: string;
  location: string;
  sort_order: number;
}

type WingKey = 'main' | 'north' | 'east' | 'south' | 'west';

export default function Home() {
  const [currentSection, setCurrentSection] = useState<'entrance' | 'gallery' | 'wing'>('entrance');
  const [currentWing, setCurrentWing] = useState<WingKey | null>(null);
  const [showUploadManager, setShowUploadManager] = useState(false);
  const [showContact, setShowContact] = useState(false);
  
  // Per-wing light intensity memory
  const [wingLightIntensity, setWingLightIntensity] = useState<Record<WingKey, number>>({
    main: 80,
    north: 70,
    east: 85,
    south: 90,
    west: 60,
  });
  
  const [selectedArtwork, setSelectedArtwork] = useState<number | null>(null);
  const [galleryArtworks, setGalleryArtworks] = useState<CustomArtwork[]>([]);

  const fetchArtworks = async () => {
    const { data, error } = await supabase
      .from('artworks')
      .select('*')
      .order('sort_order', { ascending: true });

    if (!error && data && data.length > 0) {
      const mapped: CustomArtwork[] = data.map((row: any) => ({
        id: row.id,
        title: row.title,
        artist: row.artist,
        year: row.year,
        medium: row.medium,
        dimensions: row.dimensions,
        imageUrl: row.image_url,
        description: row.description,
        location: row.location,
        sort_order: row.sort_order ?? 0,
      }));
      setGalleryArtworks(mapped);
    }
  };

  useEffect(() => {
    fetchArtworks();
  }, []);

  const handleLightChange = (intensity: number) => {
    if (currentWing) {
      setWingLightIntensity(prev => ({
        ...prev,
        [currentWing]: intensity,
      }));
    }
  };

  const getCurrentLightIntensity = () => {
    return currentWing ? wingLightIntensity[currentWing] : 80;
  };

  const enterGallery = () => setCurrentSection('gallery');
  
  const exitToEntrance = () => {
    setCurrentSection('entrance');
    setCurrentWing(null);
    setSelectedArtwork(null);
  };

  const navigateToWing = (wing: WingKey | null) => {
    if (wing === null) {
      setCurrentSection('gallery');
      setCurrentWing(null);
    } else {
      setCurrentSection('wing');
      setCurrentWing(wing);
    }
    setSelectedArtwork(null);
  };

  const returnToMainGallery = () => {
    setCurrentSection('gallery');
    setCurrentWing(null);
    setSelectedArtwork(null);
  };

  const getWingArtworks = (wing: WingKey) => {
    const wingNames: Record<WingKey, string> = {
      main: 'Main Gallery',
      north: 'North Wing',
      east: 'East Wing',
      south: 'South Wing',
      west: 'West Wing',
    };
    return galleryArtworks
      .filter((artwork) => artwork.location === wingNames[wing])
      .sort((a, b) => a.sort_order - b.sort_order);
  };

  const handleDeleteArtwork = async (id: number) => {
    await supabase.from('artworks').delete().eq('id', id);
    setGalleryArtworks(prev => prev.filter(a => a.id !== id));
  };

  const handleEditArtwork = async (artwork: CustomArtwork) => {
    const { error } = await supabase
      .from('artworks')
      .update({
        title: artwork.title,
        artist: artwork.artist,
        year: artwork.year,
        medium: artwork.medium,
        dimensions: artwork.dimensions,
        description: artwork.description,
        location: artwork.location,
        image_url: artwork.imageUrl,
      })
      .eq('id', artwork.id);

    if (error) {
      throw new Error(error.message);
    }

    setGalleryArtworks(prev =>
      prev.map(a => (a.id === artwork.id ? { ...a, ...artwork } : a))
    );
  };

  return (
    <div className="relative w-full min-h-screen bg-stone-50 overflow-hidden">
      {currentSection === 'entrance' ? (
        <EntranceHall onEnter={enterGallery} onContact={() => setShowContact(true)} />
      ) : currentSection === 'wing' && currentWing ? (
        <>
          <WingRoom
            wing={currentWing}
            artworks={getWingArtworks(currentWing)}
            lightIntensity={getCurrentLightIntensity()}
            onArtworkClick={(id) => setSelectedArtwork(id)}
            onNavigateWing={navigateToWing}
          />
          <LightControlPanel
            lightIntensity={getCurrentLightIntensity()}
            onLightChange={handleLightChange}
            currentWing={currentWing}
          />
        </>
      ) : (
        <>
          <ArtworkGallery
            lightIntensity={getCurrentLightIntensity()}
            selectedArtwork={selectedArtwork}
            onSelectArtwork={setSelectedArtwork}
            artworks={galleryArtworks}
            activeWing={currentWing}
            onAddArtwork={() => setShowUploadManager(true)}
            onNavigateToWing={navigateToWing}
            onDeleteArtwork={handleDeleteArtwork}
            onEditArtwork={handleEditArtwork}
          />
          <LightControlPanel
            lightIntensity={getCurrentLightIntensity()}
            onLightChange={handleLightChange}
            currentWing={currentWing}
          />
        </>
      )}

      {currentSection !== 'entrance' && (
        <NavigationMap
          currentSection={currentSection === 'wing' ? 'wing' : 'gallery'}
          currentWing={currentWing}
          onNavigate={exitToEntrance}
          onNavigateToWing={navigateToWing}
          onReturnToMainGallery={returnToMainGallery}
        />
      )}

      {selectedArtwork !== null && (
        <InformationOverlay
          artworkId={selectedArtwork}
          artworks={galleryArtworks}
          onClose={() => setSelectedArtwork(null)}
          onNavigate={setSelectedArtwork}
          onSaved={(updated) =>
            setGalleryArtworks(prev =>
              prev.map(a => (a.id === updated.id ? { ...a, ...updated } : a))
            )
          }
        />
      )}

      {showUploadManager && (
        <ImageUploadManager
          onSave={fetchArtworks}
          onClose={() => setShowUploadManager(false)}
          existingCount={galleryArtworks.length}
          currentWing={currentWing as any}
        />
      )}

      {/* Floating Contact Button — visible only inside the gallery */}
      {currentSection !== 'entrance' && (
        <button
          type="button"
          onClick={() => setShowContact(true)}
          className="fixed bottom-24 right-6 z-40 flex items-center gap-2 px-5 py-3 bg-white/90 backdrop-blur-sm border border-stone-200 text-stone-800 text-xs tracking-widest uppercase font-light shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-mail-line text-sm"></i>
          </div>
          Contact
        </button>
      )}

      {showContact && (
        <ContactForm onClose={() => setShowContact(false)} />
      )}
    </div>
  );
}